import React, { useMemo, useRef } from 'react';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';
import { isNil } from 'lodash-es';
import { getChartOption } from './chartOption';
import { DateType } from '../TimeSelector';
import { V2HighEnergyDeviceAnalysisV2EnergyTrendPostResponse } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import styles from './index.module.scss';

// 图表数据类型定义，与组件的业务逻辑紧密相关
type ChartDataProps = {
  sharpDataList?: { time?: number; value?: number }[];
  peakDataList?: { time?: number; value?: number }[];
  flatDataList?: { time?: number; value?: number }[];
  valleyDataList?: { time?: number; value?: number }[];
  costDataList?: { time?: number; value?: number }[];
};

export interface EnergyStackChartProps {
  data?: V2HighEnergyDeviceAnalysisV2EnergyTrendPostResponse;
  timeResolution: DateType;
  energyTrendLoading: boolean;
}

const EnergyStackChart: React.FC<EnergyStackChartProps> = ({ data, timeResolution, energyTrendLoading }) => {
  const theme = useTheme();
  const chartRef = useRef<EchartsWithThemeRef>();

  // 将API返回的数据转换为图表需要的格式
  const chartData = useMemo<ChartDataProps | null>(() => {
    if (!data?.dataPoints) return null;

    const sharpDataList = (data.dataPoints ?? []).map(dp => ({
      time: dp.time,
      value: dp.periodBreakdown?.summitValue,
      total: dp.value,
      rate: dp.periodBreakdown?.summitPercentage,
    }));

    const peakDataList = (data.dataPoints ?? []).map(dp => ({
      time: dp.time,
      value: dp.periodBreakdown?.peakValue,
      total: dp.value,
      rate: dp.periodBreakdown?.peakPercentage,
    }));

    const flatDataList = (data.dataPoints ?? []).map(dp => ({
      time: dp.time,
      value: dp.periodBreakdown?.plainValue,
      total: dp.value,
      rate: dp.periodBreakdown?.plainPercentage,
    }));

    const valleyDataList = (data.dataPoints ?? []).map(dp => ({
      time: dp.time,
      value: dp.periodBreakdown?.valleyValue,
      total: dp.value,
      rate: dp.periodBreakdown?.valleyPercentage,
    }));

    const costDataList = (data.dataPoints ?? []).map(dp => ({
      time: dp.time,
      value: dp.costValue,
    }));

    return {
      sharpDataList,
      peakDataList,
      flatDataList,
      valleyDataList,
      costDataList,
    };
  }, [data]);

  const chartOption = useMemo(() => {
    if (!chartData) return null;
    return getChartOption(
      chartData,
      timeResolution,
      'kWh',
      data?.maxValue ?? null,
      data?.minValue ?? null,
      data?.avgValue ?? null
    );
  }, [chartData, timeResolution, data]);

  const xLabel = useMemo(() => {
    if (timeResolution === DateType.YEAR) {
      return 'YYYY-MM';
    } else {
      return 'MM-DD';
    }
  }, [timeResolution]);

  return (
    <div className={styles.energyStackChart}>
      <EchartsWithTheme
        loading={energyTrendLoading}
        empty={{
          isShowEmpty: !chartData,
        }}
        style={{ height: 350 }}
        option={chartOption}
        theme={theme}
        ref={d => {
          if (d) {
            chartRef.current = d;
          }
        }}
        onEvents={{
          brushSelected: (params: any) => {
            let startTime;
            let endTime;
            let totalValue = 0; // 总值
            const brushComponent = params.batch[0];
            const chartObject = chartRef.current?.getInstance();
            const option = chartObject?.getOption();
            const series = option?.series as any[];

            const rich: any = {};
            const lines: string[] = [];

            const getPointValue = (point: any) => {
              if (Array.isArray(point)) {
                return point[1];
              }
              if (Array.isArray(point?.value)) {
                return point.value[1];
              }
              return point?.value;
            };

            brushComponent.selected.forEach((item: any) => {
              const findSeries = series?.find(i => i.name === item.seriesName);
              if (!findSeries || !Array.isArray(findSeries.data) || !item?.dataIndex?.length) {
                return;
              }

              let arr: any[] = [];
              item.dataIndex.forEach((c: number, index: number) => {
                const point = findSeries.data?.[c];
                if (!point) {
                  return;
                }

                const pointValue = getPointValue(point);

                if (index === 0) {
                  startTime = point[0];
                }
                if (index === item.dataIndex.length - 1) {
                  endTime = point[0];
                }
                arr.push(pointValue);

                // 累加总值
                if (!isNil(pointValue)) {
                  totalValue += Number(pointValue);
                }
              });

              // 当前系列总和
              const subtotal = arr.filter(x => !isNil(x)).reduce((a, b) => Number(a) + Number(b), 0);

              let color: string;

              // 如果 series 自己有颜色
              if (findSeries?.itemStyle?.color) {
                color = findSeries.itemStyle.color as string;
              } else {
                const colors = Array.isArray(option!.color) ? option!.color : [option!.color];
                color = colors[item.seriesIndex % colors.length] as string;
              }

              // 定义 rich 样式
              const markerKey = `m${item.seriesIndex}`;
              rich[markerKey] = {
                height: 10,
                width: 10,
                backgroundColor: color,
                borderRadius: 5,
              };

              // 拼接带 marker 的文本
              if (!item.seriesName.includes('series')) {
                lines.push(`{${markerKey}|} ${item.seriesName}：${subtotal.toFixed(2)} kWh`);
              }
            });

            if (brushComponent.areas.length > 0) {
              const chartWidth = chartObject?.getWidth?.() || 0;
              let left = brushComponent.areas[0].range[1] - 340; // 你原来的定位方式
              // 防止 title 超出右边界
              if (left + 340 > chartWidth) {
                left = chartWidth - 340 - 10; // 340是title宽度，10是边距
              }
              if (left < 0) {
                left = 0; // 防止超出左边界
              }
              chartObject?.setOption({
                title: {
                  show: true,
                  padding: 8,
                  backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.8)' : '#fff',
                  borderColor: 'transparent',
                  borderRadius: 2,
                  textStyle: {
                    color: theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 20,
                    rich,
                  },
                  text: [
                    `${dayjs(Number(startTime)).format(xLabel)} ~ ${dayjs(Number(endTime)).format(xLabel)}`,
                    `总用能：${totalValue.toFixed(2)} kWh`,
                    ...lines,
                  ].join('\n'),
                  top: 100,
                  left,
                  zlevel: 99999,
                },
              });
            } else {
              chartObject?.setOption({
                title: {
                  show: false,
                },
              });
            }
          },
        }}
      />
    </div>
  );
};

export default EnergyStackChart;
